// Typed client for the backend REST API.
//
// Types here mirror backend/adapter/primary/http/dto/dto.go exactly. Note what
// CameraResponse does NOT have: a password field. That is not an oversight to
// work around here — the backend never serializes it, by design, so there is
// nothing this client could read even if it wanted to.

import { getApiBaseUrl } from "./runtimeConfig";

const API_BASE_URL = getApiBaseUrl();

// Mirrors backend domain/model/camera.go's CameraVendor. The RTSP puller
// itself doesn't branch on this — it exists so the UI can offer the right
// default stream-path convention and show operators what kind of device
// they're looking at. "generic" covers any brand not yet in the catalogue —
// still a real, storable vendor, just with no known stream-path convention
// to default from.
export type CameraVendor = "axis" | "hikvision" | "generic";

/** Which of a camera's two encodings to pull. Sub is a fraction of the
 *  bandwidth — measured on this fleet, 125 kbps against 2522. */
export type StreamQuality = "main" | "sub";

export interface Camera {
  id: string;
  project_id: string;
  name: string;
  location: string;
  vendor: CameraVendor;
  host: string;
  port: number;
  stream_path: string;
  /** The device's lower-resolution second stream, empty when it has none.
   *  A video wall tile costs a fraction of the main stream's bandwidth on
   *  this path — measured on this fleet, 125 kbps against 2522. */
  sub_stream_path: string;
  /** Which encoding this camera is configured to pull. */
  stream_quality: StreamQuality;
  /** What it will ACTUALLY be pulled at. Differs from `stream_quality` when a
   *  camera set to "sub" has no sub stream to fall back on — a fallback that
   *  costs roughly twenty times the bandwidth of the stream it replaced, so
   *  the UI marks it rather than showing a setting that isn't honoured. */
  effective_stream_quality: StreamQuality;
  username: string;
  enabled: boolean;
  /** Whoever added the camera declared that it has a motorised mount. */
  ptz_enabled: boolean;
  /** The device's HTTP control port — not `port`, which is RTSP. */
  ptz_port: number;
  /** Whether control commands go over HTTPS. Some hardened cameras close
   *  port 80 entirely and answer only on 443. */
  ptz_use_tls: boolean;
  /** Derived server-side: ptz_enabled AND the backend has a control adapter
   *  for this vendor. This — not `ptz_enabled` — is what the UI should gate
   *  a control pad on, so the rule lives in one place (the backend's
   *  CameraVendor.HasPTZProtocol) rather than being re-derived here. */
  supports_ptz: boolean;
  /** The recorder this camera is a channel of, absent for a directly-connected
   *  camera. What the UI nests rows under — see groupCamerasByDevice. */
  device_id?: string;
  /** The recorder's own input channel number, absent/0 when standalone. */
  channel_id?: number;
  version: number;
  created_at: string;
  updated_at: string;
}

/** A recorder (NVR) that fronts several cameras.
 *
 *  Mirrors backend dto.DeviceResponse. Like Camera it has no password field:
 *  the credential is stored server-side and never serialized. */
export interface Device {
  id: string;
  project_id: string;
  name: string;
  location: string;
  vendor: CameraVendor;
  host: string;
  port: number;
  /** The HTTP port channel enumeration runs over — not `port`, which is RTSP. */
  http_port: number;
  use_tls: boolean;
  username: string;
  /** What the recorder last called itself, e.g. "DS-7732NXI-K4(D)". */
  model: string;
  /** Derived server-side: whether this backend can enumerate this vendor's
   *  channels. Gates the "scan" action, so the capability rule lives in one
   *  place (the backend) rather than being re-derived here. */
  supports_discovery: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface CreateDeviceInput {
  project_id: string;
  name: string;
  location: string;
  vendor: CameraVendor;
  host: string;
  port: number;
  http_port: number;
  use_tls: boolean;
  username: string;
  password: string;
}

/** Password omitted means "keep the stored credential", matching the backend's
 *  pointer field. The blast radius here is larger than for a camera: clearing
 *  it would break every camera behind the recorder at once. */
export interface UpdateDeviceInput {
  project_id: string;
  name: string;
  location: string;
  vendor?: CameraVendor;
  host: string;
  port: number;
  http_port: number;
  use_tls: boolean;
  username: string;
  password?: string;
  version: number;
}

/** A recorder the operator has typed in but not saved yet. */
export interface ProbeDeviceInput {
  vendor: CameraVendor;
  host: string;
  port: number;
  http_port: number;
  use_tls: boolean;
  username: string;
  password: string;
}

/** One camera a recorder fronts. */
export interface ScannedChannel {
  channel_id: number;
  name: string;
  /** The camera's own address behind the recorder. Shown so an operator can
   *  recognise a camera they know by IP; nothing connects to it. */
  source_host: string;
  model: string;
  online: boolean;
  stream_path: string;
  sub_stream_path: string;
  codec: string;
  width: number;
  height: number;
  /** What the recorder declared. A suggestion, not a verdict: discovery is
   *  deliberately conservative and reports third-party domes as fixed, so the
   *  wizard pre-ticks from this and lets the operator disagree. */
  ptz_capable: boolean;
  /** Set when this channel is already a camera here, so the wizard can show it
   *  as imported instead of offering a duplicate. */
  imported_camera_id?: string;
}

export interface ScanResult {
  model: string;
  channels: ScannedChannel[];
}

export interface ImportChannelInput {
  channel_id: number;
  name: string;
  /** null/undefined accepts what the scan found; a boolean overrides it. */
  ptz_enabled?: boolean | null;
  /** "main" or "sub". Omit for sub, which is the default because imported
   *  channels are destined for a wall. */
  stream_quality?: StreamQuality;
  enabled: boolean;
}

export interface ImportResult {
  created: Camera[];
  /** Channels that were already imported. Reported rather than dropped, so an
   *  operator who ticked ten and got three can see which three existed. */
  skipped_channels: number[];
}

/** One camera whose label on the recorder changed. */
export interface SyncedRename {
  camera_id: string;
  channel_id: number;
  old_name: string;
  new_name: string;
}

/** An imported camera the recorder has stopped reporting. */
export interface MissingChannel {
  camera_id: string;
  channel_id: number;
  name: string;
}

/** What a sync changed, and what it deliberately did not.
 *
 *  Only `renamed` and `learned_sub_streams` describe writes. Everything else
 *  is reported so the operator can decide: a sync never creates or deletes
 *  cameras. */
export interface SyncResult {
  renamed: SyncedRename[];
  kept_local_names: SyncedRename[];
  /** Cameras that gained a sub-stream path they did not have before — the
   *  only visible outcome on a recorder whose labels have not changed. */
  learned_sub_streams: number;
  new_channels: ScannedChannel[];
  missing_channels: MissingChannel[];
}

export interface DetachResult {
  detached_cameras: number;
}

export interface CreateCameraInput {
  name: string;
  project_id: string;
  location: string;
  vendor: CameraVendor;
  host: string;
  port: number;
  stream_path: string;
  sub_stream_path: string;
  stream_quality: StreamQuality;
  username: string;
  password: string;
  enabled: boolean;
  ptz_enabled: boolean;
  ptz_port: number;
  ptz_use_tls: boolean;
}

// Password is optional: omitting it means "keep the stored credential",
// matching the backend's UpdateCameraRequest (a pointer field there). Vendor
// is likewise optional here for the same "empty means keep the stored one"
// convention — see CameraUsecase.Update on the backend.
export interface UpdateCameraInput {
  name: string;
  project_id: string;
  location: string;
  vendor?: CameraVendor;
  host: string;
  port: number;
  stream_path: string;
  /** Optional for a different reason than `password`: omitting it keeps the
   *  stored path, while an explicit "" clears it. A camera can genuinely lose
   *  its second stream, which the empty-means-keep convention used by
   *  `stream_path` could never express. */
  sub_stream_path?: string;
  /** Omit to keep the stored quality. */
  stream_quality?: StreamQuality;
  username: string;
  password?: string;
  // Present here although `enabled` is not: PTZ describes the hardware, not
  // a runtime state, so it is an ordinary form field. See the backend's
  // model.Camera.PTZEnabled for that distinction.
  ptz_enabled: boolean;
  ptz_port: number;
  ptz_use_tls: boolean;
  version: number;
}

// Project ("site" — used interchangeably at this contractor) is the
// DMS-style grouping cameras belong to, and the unit CameraScope grants
// (below) are expressed in terms of.
/** The system-wide cap on how many cameras may be pulled at once.
 *  Zero means no global cap, leaving each site's own limit as the only one. */
export interface StreamLimits {
  max_global_streams: number;
  version: number;
  updated_at: string;
}

/** Single sign-on configuration — mirrors backend dto.OIDCSettingsResponse.
 *  Never carries the actual client secret, only whether one is set: the
 *  backend never serializes it back out once saved, the same discipline
 *  Camera/Device responses apply to stored credentials. */
export interface OIDCSettings {
  enabled: boolean;
  display_name: string;
  issuer_url: string;
  client_id: string;
  client_secret_set: boolean;
  default_role_id: string;
  /** The fixed callback URL to register with the identity provider. */
  redirect_uri: string;
  version: number;
  updated_at: string;
}

/** client_secret omitted/undefined means "keep the stored secret" — matches
 *  the backend's pointer-field convention (UpdateOIDCSettingsInput.ClientSecret
 *  on the server). Send an explicit "" to clear a previously-set secret. */
export interface UpdateOIDCSettingsInput {
  enabled: boolean;
  display_name: string;
  issuer_url: string;
  client_id: string;
  client_secret?: string;
  default_role_id: string;
  version: number;
}

/** What the login page needs to decide whether to render a "Sign in
 *  with..." button — the only OIDC configuration visible with no session. */
export interface OIDCStatus {
  enabled: boolean;
  display_name: string;
}

/** One camera currently counted against the quota. `name` is empty for a
 *  camera the caller may not see — the server names only what a scoped read
 *  already returned. */
export interface ActiveStream {
  camera_id: string;
  project_id: string;
  name: string;
  started_at: string;
  viewers: number;
}

/** Body of a refused start (HTTP 409). Carries the numbers and the stream that
 *  would have to stop, because "the wall is full" on its own leaves an
 *  operator with nothing to do but press play again. */
export interface QuotaExhausted {
  error: string;
  scope: "project" | "global";
  limit: number;
  candidate: ActiveStream | null;
}

/** Broadcast to every session when something happens to a running stream that
 *  other people need to know about — today, an eviction. */
export interface StreamNotice {
  type: "stream_evicted" | "eviction_requested" | "eviction_answered";
  camera_id: string;
  camera_name: string;
  project_id: string;
  started_camera_id: string;
  started_camera_name: string;
  actor: string;
  /** Ties an answer back to the request that produced it, so a requester's own
   *  browser can recognise the reply to its own question. */
  request_id: string;
  /** Meaningful only on "eviction_answered". */
  approved: boolean;
  at: string;
}

/** A viewer asking somebody who may stop streams to free a slot for them. */
export interface EvictionRequest {
  id: string;
  requester_id: string;
  requester_name: string;
  camera_id: string;
  camera_name: string;
  evict_camera_id: string;
  evict_camera_name: string;
  project_id: string;
  created_at: string;
}

export interface Project {
  id: string;
  code: string;
  name: string;
  location: string;
  // IANA identifier, e.g. "Asia/Jakarta" — the site's local timezone.
  timezone: string;
  is_active: boolean;
  /** Caps how many of this site's cameras may be pulled at once. Zero means
   *  no site-specific limit — only the global cap applies. */
  max_concurrent_streams: number;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectInput {
  code: string;
  name: string;
  location: string;
  timezone: string;
  max_concurrent_streams: number;
}

export interface UpdateProjectInput {
  code: string;
  name: string;
  location: string;
  timezone: string;
  max_concurrent_streams: number;
  version: number;
}

// CameraScope restricts which cameras a role or user may see or act on —
// mirrors backend domain/model/camera_scope.go exactly. Both arrays empty
// is deliberately "unrestricted" (see isUnrestrictedScope below), matching
// the backend's zero-value semantics.
export interface CameraScope {
  project_ids: string[];
  camera_ids: string[];
}

export const unrestrictedScope: CameraScope = { project_ids: [], camera_ids: [] };

/** Mirrors CameraScope.IsUnrestricted on the backend. */
export function isUnrestrictedScope(scope: CameraScope): boolean {
  return scope.project_ids.length === 0 && scope.camera_ids.length === 0;
}

export interface StartStreamResponse {
  lease_id: string;
  camera_id: string;
  expires_at: string;
  playlist_url: string;
}

export type StreamState = "idle" | "connecting" | "active" | "ready" | "failed";

/** Whether the server's background monitor can reach a camera — a different
 *  question from StreamState, which is only whether the server is pulling its
 *  stream. Mirrors the backend's model.ReachabilityState. */
export type Reachability = "online" | "offline" | "unknown";

/** Why a camera has the reachability it has. A fixed set on purpose: the
 *  backend never sends error text here. Mirrors model.ReachabilityReason. */
export type ReachabilityReason =
  | "channel_offline"
  | "recorder_unreachable"
  | "recorder_auth_failed"
  | "recorder_auth_unavailable"
  | "port_unreachable"
  | "not_checked";

export interface StreamStatus {
  camera_id: string;
  state: StreamState;
  viewer_count: number;
  bitrate_kbps: number;
  reconnect_count: number;
  last_error?: string;
  updated_at: string;
  /** Absent when the monitor has no answer for the camera (it is disabled,
   *  newly added, or the monitor is switched off). Absent reads as unknown,
   *  never as online. */
  reachability?: Reachability;
  reachability_reason?: ReachabilityReason;
  reachability_checked_at?: string;
}

/** A continuous-move command. Each axis is a normalized velocity in [-1, 1]
 *  — mirrors the backend's model.PTZVector, including its sign conventions:
 *  pan > 0 is right, tilt > 0 is up, zoom > 0 is in. An all-zero vector is
 *  rejected by the backend; stopping has its own endpoint. */
export interface PTZVector {
  pan: number;
  tilt: number;
  zoom: number;
}

/** A position stored on the camera itself.
 *
 *  Presets are not this app's data: they are created and named through the
 *  camera's own web interface, so there is no endpoint here that makes one.
 *  The list is read live from the device — which is what makes "put this
 *  camera back where it was" possible after an operator has moved it. */
export interface PTZPreset {
  id: number;
  name: string;
}

export type AuthProvider = "local" | "oidc";

// Permission is a fixed capability key from the backend's catalog (see
// domain/model/permission.go) — fetched via api.permissions.list() rather
// than hardcoded here, so this list can never drift from what the backend
// actually enforces.
export type Permission = string;

// RoleSummary is the role info embedded in a User — see backend
// dto.RoleSummary. Permissions travel with it so the frontend can gate its
// own UI (e.g. hiding the Users tab) from the current user's own /auth/me
// response without a second request.
export interface RoleSummary {
  id: string;
  name: string;
  permissions: Permission[];
  camera_scope: CameraScope;
}

export interface Role extends RoleSummary {
  is_system: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface PermissionCatalogEntry {
  key: Permission;
  label: string;
  description: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  display_name: string;
  auth_provider: AuthProvider;
  role: RoleSummary;
  // null means "no override, inherit the role's camera_scope" — distinct
  // from an override that explicitly narrows access, per the backend's
  // EffectiveCameraScope.
  camera_scope_override: CameraScope | null;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

/** Reports whether user's role grants permission — mirrors User.HasPermission
 *  on the backend. Only ever used for UI gating (show/hide a tab, a button);
 *  the backend re-checks every actual mutation regardless. */
export function hasPermission(user: User | null, permission: Permission): boolean {
  return user?.role.permissions.includes(permission) ?? false;
}

export interface CreateUserInput {
  username: string;
  email: string;
  display_name: string;
  password: string;
  role_id: string;
}

// role_id is here (unlike UpdateProfileInput below) because this is the
// admin edit-user form — see UpdateUserRequest on the backend for why
// password and is_active are absent instead: they are state transitions
// with their own endpoints.
export interface UpdateUserInput {
  username: string;
  email: string;
  display_name: string;
  role_id: string;
  version: number;
}

export interface CreateRoleInput {
  name: string;
  permissions: Permission[];
  camera_scope: CameraScope;
}

export interface UpdateRoleInput {
  name: string;
  permissions: Permission[];
  camera_scope: CameraScope;
  version: number;
}

// The self-service profile form. Deliberately has no role or is_active
// field — there is nothing here for a signed-in user to escalate even by
// accident, because the type has no place to put it.
export interface UpdateProfileInput {
  display_name: string;
  email: string;
  version: number;
}

/** Thrown for any non-2xx response, carrying the server's error message. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The decoded error body, when there was one.
     *
     *  Some refusals carry the detail that makes them actionable — a stream
     *  quota refusal names which limit was hit and what would have to stop —
     *  and reducing every failure to its message string throws that away. */
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Narrows an error to a stream-quota refusal, with its detail intact. */
export function asQuotaExhausted(err: unknown): QuotaExhausted | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as Partial<QuotaExhausted> | undefined;
  if (!body || (body.scope !== "project" && body.scope !== "global")) return null;
  return body as QuotaExhausted;
}

let unauthorizedHandler: (() => void) | null = null;

/** Registered once by AuthProvider; see the 401 handling inside request(). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

/** Fires the same session-ended fallback `request()` uses below, for a 401
 *  this client learns about some other way than fetch() — namely hls.js's
 *  own XHR loader (see useHlsStream.ts), which carries the session cookie
 *  the same as every other request but never passes through request() itself.
 *  Without this, a session ending mid-stream showed as a decode-looking
 *  dead end instead of the login screen every other 401 in the app falls
 *  back to. */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    // Every route but /auth/login sits behind the session cookie now —
    // without this, a cross-origin request (the dev server on a different
    // port, or any deployment where the API isn't same-origin) omits it and
    // every call 401s regardless of whether the user is signed in.
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
  });

  // A 401 on any route other than login itself means the session has ended
  // (expired, revoked, or the account was disabled) — not a login attempt
  // that simply failed. AuthProvider registers a handler here so the app can
  // fall back to the login screen from anywhere, without every call site
  // needing to know how to do that.
  if (res.status === 401 && path !== "/api/v1/auth/login") {
    notifyUnauthorized();
  }

  if (!res.ok) {
    let message = res.statusText;
    let body: unknown;
    try {
      body = await res.json();
      const asError = body as { error?: string };
      if (asError.error) message = asError.error;
    } catch {
      // Body wasn't JSON (or was empty) — the status text is all we have.
    }
    throw new ApiError(res.status, message, body);
  }

  if (res.status === 204 || res.status === 205) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  cameras: {
    list: () => request<Camera[]>("/api/v1/cameras"),
    get: (id: string) => request<Camera>(`/api/v1/cameras/${id}`),
    create: (input: CreateCameraInput) =>
      request<Camera>("/api/v1/cameras", { method: "POST", body: JSON.stringify(input) }),
    update: (id: string, input: UpdateCameraInput) =>
      request<Camera>(`/api/v1/cameras/${id}`, { method: "PUT", body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/api/v1/cameras/${id}`, { method: "DELETE" }),
    enable: (id: string, version: number) =>
      request<Camera>(`/api/v1/cameras/${id}/enable`, {
        method: "POST",
        body: JSON.stringify({ version }),
      }),
    disable: (id: string, version: number) =>
      request<Camera>(`/api/v1/cameras/${id}/disable`, {
        method: "POST",
        body: JSON.stringify({ version }),
      }),
  },
  devices: {
    list: () => request<Device[]>("/api/v1/devices"),
    get: (id: string) => request<Device>(`/api/v1/devices/${id}`),
    create: (input: CreateDeviceInput) =>
      request<Device>("/api/v1/devices", { method: "POST", body: JSON.stringify(input) }),
    update: (id: string, input: UpdateDeviceInput) =>
      request<Device>(`/api/v1/devices/${id}`, { method: "PUT", body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/api/v1/devices/${id}`, { method: "DELETE" }),

    // POST rather than GET although it stores nothing: it carries a password,
    // and a credential in a query string ends up in access logs and browser
    // history. See DeviceHandler.Probe.
    probe: (input: ProbeDeviceInput) =>
      request<ScanResult>("/api/v1/devices/probe", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    // Also POST: it makes the server open a connection to a device, which no
    // prefetch or speculative load should be able to trigger.
    scan: (id: string) => request<ScanResult>(`/api/v1/devices/${id}/scan`, { method: "POST" }),

    import: (id: string, channels: ImportChannelInput[]) =>
      request<ImportResult>(`/api/v1/devices/${id}/import`, {
        method: "POST",
        body: JSON.stringify({ channels }),
      }),

    // Reconciles the imported cameras with what the recorder reports now.
    // Adopts changed labels for cameras nobody renamed here; reports new and
    // missing channels without acting on them.
    sync: (id: string) => request<SyncResult>(`/api/v1/devices/${id}/sync`, { method: "POST" }),

    // Turns the recorder's cameras into ordinary directly-connected ones,
    // which keep streaming. The escape hatch that makes delete's refusal
    // reasonable.
    detachCameras: (id: string) =>
      request<DetachResult>(`/api/v1/devices/${id}/detach-cameras`, { method: "POST" }),
  },
  ptz: {
    // A continuous move: the mount keeps going until stop() or until the
    // server's watchdog fires. Callers must therefore either guarantee a
    // stop or keep re-sending this — see usePtzControl, which does both.
    move: (cameraId: string, vector: PTZVector) =>
      request<void>(`/api/v1/cameras/${cameraId}/ptz/move`, { method: "POST", body: JSON.stringify(vector) }),
    stop: (cameraId: string) => request<void>(`/api/v1/cameras/${cameraId}/ptz/stop`, { method: "POST" }),
    // Reaches out to the camera on every call — presets live on the device,
    // so a copy cached here would go stale the moment somebody stores one
    // through its own interface. usePtzPresets holds the result briefly.
    listPresets: (cameraId: string) => request<PTZPreset[]>(`/api/v1/cameras/${cameraId}/ptz/presets`),
    gotoPreset: (cameraId: string, preset: number) =>
      request<void>(`/api/v1/cameras/${cameraId}/ptz/presets/${preset}/goto`, { method: "POST" }),
  },
  stream: {
    start: (cameraId: string) =>
      request<StartStreamResponse>(`/api/v1/cameras/${cameraId}/stream/start`, { method: "POST" }),
    // leaseId travels as a query param, not just a header: page-unload paths
    // use fetch with keepalive, where a custom header is not always honoured,
    // and the backend accepts either — see leaseIDFrom on the server.
    stop: (cameraId: string, leaseId: string) =>
      request<void>(`/api/v1/cameras/${cameraId}/stream/stop?lease_id=${leaseId}`, { method: "POST" }),
    renew: (cameraId: string, leaseId: string) =>
      request<void>(`/api/v1/cameras/${cameraId}/stream/renew?lease_id=${leaseId}`, { method: "PUT" }),
    /** Starts a camera by stopping a specific running one. Requires
     *  cameras.manage; the candidate must be the one the caller was offered. */
    startEvicting: (cameraId: string, evictCameraId: string) =>
      request<StartStreamResponse>(
        `/api/v1/cameras/${cameraId}/stream/start-evicting?evict=${encodeURIComponent(evictCameraId)}`,
        { method: "POST" },
      ),
    status: (cameraId: string) => request<StreamStatus>(`/api/v1/cameras/${cameraId}/stream/status`),
  },
  streamRequests: {
    /** Ask for a slot. Needs only cameras.view — asking is not the
     *  privileged act; answering is. */
    create: (input: { camera_id: string; evict_camera_id: string }) =>
      request<EvictionRequest>("/api/v1/stream-requests", { method: "POST", body: JSON.stringify(input) }),
    list: () => request<EvictionRequest[]>("/api/v1/stream-requests"),
    approve: (id: string) =>
      request<EvictionRequest>(`/api/v1/stream-requests/${id}/approve`, { method: "POST" }),
    reject: (id: string) =>
      request<EvictionRequest>(`/api/v1/stream-requests/${id}/reject`, { method: "POST" }),
  },
  streams: {
    /** What is running right now, scoped to what the caller may see. */
    active: () => request<ActiveStream[]>("/api/v1/streams/active"),
  },
  settings: {
    streamLimits: () => request<StreamLimits>("/api/v1/settings/stream-limits"),
    updateStreamLimits: (input: { max_global_streams: number; version: number }) =>
      request<StreamLimits>("/api/v1/settings/stream-limits", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    oidc: () => request<OIDCSettings>("/api/v1/settings/oidc"),
    updateOidc: (input: UpdateOIDCSettingsInput) =>
      request<OIDCSettings>("/api/v1/settings/oidc", { method: "PUT", body: JSON.stringify(input) }),
  },
  hlsPlaylistUrl: (cameraId: string) => `${API_BASE_URL}/api/v1/cameras/${cameraId}/hls/index.m3u8`,
  healthEventsUrl: () => `${API_BASE_URL}/api/v1/health/events`,
  auth: {
    login: (usernameOrEmail: string, password: string) =>
      request<User>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ username_or_email: usernameOrEmail, password }),
      }),
    logout: () => request<void>("/api/v1/auth/logout", { method: "POST" }),
    me: () => request<User>("/api/v1/auth/me"),
    updateProfile: (input: UpdateProfileInput) =>
      request<User>("/api/v1/auth/me", { method: "PUT", body: JSON.stringify(input) }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request<void>("/api/v1/auth/me/password", {
        method: "PUT",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      }),
    oidcStatus: () => request<OIDCStatus>("/api/v1/auth/oidc/status"),
    // Not a request() call — this is a full-page navigation, not a fetch.
    // The backend redirects the browser on to the identity provider from
    // here, so LoginPage assigns window.location.href to this directly.
    // returnTo must be a same-app relative path; the backend drops anything
    // else, so there's nothing to validate client-side.
    oidcLoginUrl: (returnTo?: string) =>
      `${API_BASE_URL}/api/v1/auth/oidc/login${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ""}`,
  },
  users: {
    list: () => request<User[]>("/api/v1/users"),
    get: (id: string) => request<User>(`/api/v1/users/${id}`),
    create: (input: CreateUserInput) => request<User>("/api/v1/users", { method: "POST", body: JSON.stringify(input) }),
    update: (id: string, input: UpdateUserInput) =>
      request<User>(`/api/v1/users/${id}`, { method: "PUT", body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/api/v1/users/${id}`, { method: "DELETE" }),
    enable: (id: string, version: number) =>
      request<User>(`/api/v1/users/${id}/enable`, { method: "POST", body: JSON.stringify({ version }) }),
    disable: (id: string, version: number) =>
      request<User>(`/api/v1/users/${id}/disable`, { method: "POST", body: JSON.stringify({ version }) }),
    resetPassword: (id: string, newPassword: string, version: number) =>
      request<void>(`/api/v1/users/${id}/password`, {
        method: "PUT",
        body: JSON.stringify({ new_password: newPassword, version }),
      }),
    // The DMS-style per-account camera restriction override. cameraScope
    // null clears the override (inherit the role's scope again).
    setCameraScope: (id: string, cameraScope: CameraScope | null, version: number) =>
      request<User>(`/api/v1/users/${id}/camera-scope`, {
        method: "PUT",
        body: JSON.stringify({ camera_scope: cameraScope, version }),
      }),
  },
  roles: {
    list: () => request<Role[]>("/api/v1/roles"),
    create: (input: CreateRoleInput) => request<Role>("/api/v1/roles", { method: "POST", body: JSON.stringify(input) }),
    update: (id: string, input: UpdateRoleInput) =>
      request<Role>(`/api/v1/roles/${id}`, { method: "PUT", body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/api/v1/roles/${id}`, { method: "DELETE" }),
  },
  permissions: {
    list: () => request<PermissionCatalogEntry[]>("/api/v1/permissions"),
  },
  projects: {
    list: () => request<Project[]>("/api/v1/projects"),
    get: (id: string) => request<Project>(`/api/v1/projects/${id}`),
    create: (input: CreateProjectInput) =>
      request<Project>("/api/v1/projects", { method: "POST", body: JSON.stringify(input) }),
    update: (id: string, input: UpdateProjectInput) =>
      request<Project>(`/api/v1/projects/${id}`, { method: "PUT", body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/api/v1/projects/${id}`, { method: "DELETE" }),
    enable: (id: string, version: number) =>
      request<Project>(`/api/v1/projects/${id}/enable`, { method: "POST", body: JSON.stringify({ version }) }),
    disable: (id: string, version: number) =>
      request<Project>(`/api/v1/projects/${id}/disable`, { method: "POST", body: JSON.stringify({ version }) }),
  },
};

/** Sends a stop request that survives page unload, per the backend's contract. */
export function stopStreamOnUnload(cameraId: string, leaseId: string): void {
  const url = `${API_BASE_URL}/api/v1/cameras/${cameraId}/stream/stop?lease_id=${leaseId}`;
  // keepalive rather than navigator.sendBeacon: sendBeacon cannot express a
  // method other than POST-with-a-body-as-the-payload cleanly for this shape,
  // and keepalive fetch already does what's needed — survive the page tearing
  // down while the request is in flight. credentials: "include" for the same
  // reason as request() — this route sits behind requireAuth too.
  void fetch(url, { method: "POST", keepalive: true, credentials: "include" }).catch(() => {
    // Nothing to do: the page is unloading, there is no one left to tell.
  });
}

/** Stops a PTZ move on page unload, using the same keepalive trick as
 *  stopStreamOnUnload.
 *
 *  This matters more than the stream equivalent: a stream left running wastes
 *  bandwidth until its lease lapses, whereas a move left running physically
 *  turns a camera away from whatever it was installed to watch. The backend's
 *  watchdog is the real guarantee — this just makes the camera stop within
 *  milliseconds instead of seconds when the tab closes cleanly. */
export function stopPtzOnUnload(cameraId: string): void {
  const url = `${API_BASE_URL}/api/v1/cameras/${cameraId}/ptz/stop`;
  void fetch(url, { method: "POST", keepalive: true, credentials: "include" }).catch(() => {
    // Nothing to do: the page is unloading, there is no one left to tell.
  });
}
